/**
 * Card de produto: o mesmo em toda listagem (vitrine, coleção, busca, relacionados,
 * favoritos). Os padrões desenham o card de antes dos temas: foto quadrada com zoom ao
 * passar o mouse, texto à esquerda, "De ... por" na promoção, parcelas, favoritar no
 * canto e o botão de comprar sempre à vista.
 *
 * "Ao passar o mouse" não existe no celular: lá vale o estado normal (o botão que só
 * aparece ao passar fica sempre à vista, e a segunda foto vem ao deslizar).
 */
export const THEME_PRODUCT_CARD_FIELDS = {
    layout: {
        type: 'select',
        label: 'Disposição',
        help: 'Horizontal serve para vitrines de 1 ou 2 colunas; em grade estreita a foto fica pequena.',
        options: [
            { value: 'vertical', label: 'Foto em cima' },
            { value: 'horizontal', label: 'Foto à esquerda' },
        ],
        default: 'vertical',
    },
    imageRatio: {
        type: 'select',
        label: 'Formato da foto',
        options: [
            { value: 'square', label: 'Quadrada' },
            { value: 'portrait', label: 'Retrato (3:4)' },
            { value: 'landscape', label: 'Paisagem (4:3)' },
        ],
        default: 'square',
    },
    textAlign: {
        type: 'select',
        label: 'Alinhamento do texto',
        options: [
            { value: 'left', label: 'À esquerda' },
            { value: 'center', label: 'Centralizado' },
        ],
        default: 'left',
    },
    frame: {
        type: 'select',
        label: 'Moldura',
        options: [
            { value: 'card', label: 'Fundo branco com a sombra do estilo' },
            { value: 'border', label: 'Borda fina' },
            { value: 'gray', label: 'Fundo cinza' },
            { value: 'none', label: 'Sem moldura' },
        ],
        default: 'card',
    },
    buyButton: {
        type: 'select',
        label: 'Botão de comprar',
        options: [
            { value: 'always', label: 'Sempre à vista' },
            { value: 'hover', label: 'Ao passar o mouse' },
            { value: 'never', label: 'Não mostrar' },
        ],
        default: 'always',
    },
    quickBuy: {
        type: 'toggle',
        label: 'Compra rápida',
        help: 'Produto com opções (tamanho, cor) abre uma janela para escolher e pôr no carrinho, sem sair da lista. Precisa do botão de comprar.',
        default: false,
    },
    favorite: {
        type: 'select',
        label: 'Favoritar',
        options: [
            { value: 'top-right', label: 'No canto direito da foto' },
            { value: 'top-left', label: 'No canto esquerdo da foto' },
            { value: 'hidden', label: 'Não mostrar' },
        ],
        default: 'top-right',
    },
    variationThumbs: {
        group: 'Variações',
        type: 'toggle',
        label: 'Miniaturas das variações',
        help: 'A foto de cada opção (ou o nome, quando não tem foto). Passar o mouse ou tocar troca a foto do card, e o produto abre com a opção escolhida.',
        default: false,
    },
    variationThumbsMax: {
        group: 'Variações',
        type: 'range',
        label: 'Miniaturas no card',
        help: 'As demais aparecem como "+N".',
        min: 3,
        max: 8,
        step: 1,
        default: 5,
        visibleWhen: { field: 'variationThumbs', equals: true },
    },
    cardHover: {
        group: 'Ao passar o mouse',
        type: 'select',
        label: 'O card',
        options: [
            { value: 'shadow', label: 'Ganha sombra' },
            { value: 'lift', label: 'Sobe com sombra' },
            { value: 'border', label: 'Ganha borda na cor principal' },
            { value: 'none', label: 'Nada' },
        ],
        default: 'shadow',
    },
    imageHover: {
        group: 'Ao passar o mouse',
        type: 'select',
        label: 'A foto',
        help: 'Sem segunda foto, a troca não acontece. No celular a segunda foto vem ao deslizar.',
        options: [
            { value: 'zoom', label: 'Aproxima' },
            { value: 'second-photo', label: 'Troca pela segunda foto' },
            { value: 'darken', label: 'Escurece com o botão por cima' },
            { value: 'none', label: 'Nada' },
        ],
        default: 'zoom',
    },
    priceColor: {
        group: 'Preço',
        type: 'select',
        label: 'Cor do preço',
        options: [
            { value: 'primary', label: 'Cor principal' },
            { value: 'text', label: 'Cor do texto' },
            { value: 'accent', label: 'Cor de destaque' },
        ],
        default: 'primary',
    },
    priceSize: {
        group: 'Preço',
        type: 'select',
        label: 'Tamanho do preço',
        options: [
            { value: 'normal', label: 'Normal' },
            { value: 'large', label: 'Grande' },
        ],
        default: 'normal',
    },
    oldPrice: {
        group: 'Preço',
        type: 'select',
        label: 'Preço com desconto',
        options: [
            { value: 'from-to', label: '"De ... por", em duas linhas' },
            { value: 'strike', label: 'Preço antigo riscado ao lado' },
            { value: 'strike-percent', label: 'Riscado ao lado, com o percentual' },
        ],
        default: 'from-to',
    },
    installments: {
        group: 'Preço',
        type: 'toggle',
        label: 'Mostrar o parcelamento',
        help: 'Pela política financeira da empresa, a mesma do pagamento.',
        default: true,
    },
    discountBadge: {
        group: 'Selos na foto',
        type: 'select',
        label: 'Selo de desconto',
        options: [
            { value: 'promotions', label: 'Só nas promoções da loja' },
            { value: 'any', label: 'Em todo produto com desconto, com o percentual' },
            { value: 'none', label: 'Não mostrar' },
        ],
        default: 'promotions',
    },
    campaignBadge: {
        group: 'Selos na foto',
        type: 'toggle',
        label: 'Selo de campanha da coleção',
        help: 'O texto cadastrado na coleção manual (ex.: "Ofertas da semana"), nos produtos dela.',
        default: true,
    },
    soldOutBadge: { group: 'Selos na foto', type: 'toggle', label: 'Selo "Esgotado"', default: false },
    newBadge: {
        group: 'Selos na foto',
        type: 'toggle',
        label: 'Selo "Novo"',
        help: 'Nos produtos cadastrados há pouco tempo.',
        default: false,
    },
    newBadgeDays: {
        group: 'Selos na foto',
        type: 'range',
        label: 'Novo por',
        min: 7,
        max: 90,
        step: 1,
        unit: ' dias',
        default: 30,
        visibleWhen: { field: 'newBadge', equals: true },
    },
};
//# sourceMappingURL=product-card.js.map